/**
 * QuotaWarningsSection - Quota usage warnings for authenticated users
 *
 * Extracted from index.tsx to maintain <300 line limit
 * Contains: QuotaWarningBanner, QuotaWarningToast, QuotaWarningModal
 *
 * Quota Warning Strategy (Phase 2):
 * - 50% threshold: Banner (dismissible, low urgency)
 * - 80% threshold: Toast (auto-dismiss, medium urgency)
 * - 90% threshold: Modal (requires action, high urgency)
 *
 * Updated 2025-11-25: Added tier-aware messaging support
 */
import { QuotaWarningBanner } from "../../../components/quota/QuotaWarningBanner";
import { QuotaWarningToast } from "../../../components/quota/QuotaWarningToast";
import { QuotaWarningModal, type QuotaWarningTier } from "../../../components/quota/QuotaWarningModal";

interface QuotaWarningsSectionProps {
  activeWarning: {
    level: '50_percent' | '80_percent' | '90_percent';
    type: 'banner' | 'toast' | 'modal';
  } | null;
  quotaData: {
    dailyAnalysesUsed: number;
    dailyAnalysesLimit: number;
    formattedResetTime: string;
  } | null;
  isAuthenticated: boolean;
  /** Current tier for tier-aware modal messaging */
  currentTier?: QuotaWarningTier;
  /** Number of family members (for family_member tier interpolation) */
  familyMemberCount?: number;
  onCloseWarning: () => void;
  onNavigateToSubscription: () => void;
}

export function QuotaWarningsSection({
  activeWarning,
  quotaData,
  isAuthenticated: _isAuthenticated, // Reserved for future conditional rendering
  currentTier = 'free',
  familyMemberCount,
  onCloseWarning,
  onNavigateToSubscription,
}: QuotaWarningsSectionProps) {
  if (!quotaData) return null;

  return (
    <>
      {/* Quota Warning Banner (50% threshold) - Phase 2 */}
      {activeWarning?.type === 'banner' && (
        <QuotaWarningBanner
          dailyAnalysesUsed={quotaData.dailyAnalysesUsed}
          dailyAnalysesLimit={quotaData.dailyAnalysesLimit}
          resetTime={quotaData.formattedResetTime}
          onDismiss={onCloseWarning}
        />
      )}

      {/* Quota Warning Toast (80% threshold) - Phase 2 */}
      {activeWarning?.type === 'toast' && (
        <QuotaWarningToast
          dailyAnalysesUsed={quotaData.dailyAnalysesUsed}
          dailyAnalysesLimit={quotaData.dailyAnalysesLimit}
          onDismiss={onCloseWarning}
          onViewPlans={onNavigateToSubscription}
        />
      )}

      {/* Quota Warning Modal (90% threshold) - Phase 2 */}
      {/* Updated 2025-11-25: Tier-aware messaging */}
      {activeWarning?.type === 'modal' && (
        <QuotaWarningModal
          visible={true}
          dailyAnalysesUsed={quotaData.dailyAnalysesUsed}
          dailyAnalysesLimit={quotaData.dailyAnalysesLimit}
          currentTier={currentTier}
          familyMemberCount={familyMemberCount}
          onDismiss={onCloseWarning}
          onUpgrade={onNavigateToSubscription}
        />
      )}
    </>
  );
}
